import React, { useState } from 'react';

interface TextareaProps {
    id?: string,
    children?: React.ReactNode,
    defaultValue?: string,
    value?: string,
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
    className?: string,
    name: string,
    preTextarea?: string,
    innerPadding?: string,
    readOnly?: boolean,
    icon?: React.ReactNode,
    required?: boolean,
    onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void,
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void,
    onClick?: (e: React.MouseEvent<HTMLTextAreaElement>) => void,
    placeholder?: string,
    rows?: number,
}

export function Textarea({
    id,
    children,
    defaultValue,
    value,
    onChange,
    className = '',
    name,
    preTextarea,
    innerPadding,
    readOnly = false,
    icon,
    required = false,
    onFocus,
    onBlur,
    onClick,
    placeholder = '',
    rows = 5,
}: TextareaProps) {
    const [isFocused, setIsFocused] = useState(false);

    return (
        <div className={`relative w-full flex flex-row-reverse items-center ${className ? className : ''}`}>
            <textarea id={id}
                   readOnly={readOnly}
                   name={name}
                   {...(required ? { required } : {})}
                   {...(value !== undefined ? { value } : { defaultValue })}
                   onChange={onChange}
                   onClick={onClick}
                   rows={rows}
                   placeholder={children ? (isFocused ? placeholder : '') : placeholder}
                   onFocus={(e) => {
                       setIsFocused(true);
                       if (onFocus) onFocus(e);
                   }}
                   onBlur={(e) => {
                       setIsFocused(false);
                       if (onBlur) onBlur(e);
                   }}
                   className={`peer w-full h-full bg-transparent peer:border-l-transparent text-brand-green outline outline-0 focus:outline-0 disabled:bg-blue-gray-50 disabled:border-0 transition-all placeholder-[#A5A5A5] placeholder-shown:border placeholder-shown:border-[#dde1dd] placeholder-shown:border-t-[#dde1dd] border pt-3 resize-none ${children ? 'border-t-transparent focus:border-t-transparent' : ''} text-base pb-3 rounded-[4px] md:rounded-[8px] border-[#dde1dd] focus:border-link-blue ${preTextarea ? `border-l-0 rounded-l-none md:rounded-l-none pl-0.5 ${icon ? 'pr-7' : 'pr-3 md:pr-4'}` : `pl-3 md:pl-4 ${icon ? 'pr-7 cursor-pointer' : 'pr-3 md:pr-4'}`}`}>
            </textarea>
            {preTextarea && (
                <span className={`peer h-full bg-transparent text-brand-green/[.35] font-semibold outline outline-0 focus:outline-0 disabled:bg-blue-gray-50 disabled:border-0 transition-all placeholder-shown:border placeholder-shown:border-[#dde1dd] placeholder-shown:border-t-[#dde1dd] border-t border-b border-l pt-3 ${children ? 'border-t-transparent focus:border-t-transparent' : 'peer-focus:border-t-link-blue'} text-base leading-none md:leading-[1.375] ${innerPadding ? innerPadding : 'pl-3 md:pl-4 pb-3'} rounded-tl-[4px] rounded-bl-[4px] md:rounded-tl-[8px] md:rounded-bl-[8px] border-[#dde1dd] peer-focus:border-l-link-blue peer-focus:border-b-link-blue`}>{preTextarea}</span>
            )}
            {children && (
                <label className="flex w-full h-full select-none pointer-events-none absolute left-0 font-normal truncate peer-placeholder-shown:text-blue-gray-500 leading-[1.2] md:leading-none peer-focus:leading-[1.2] md:peer-focus:leading-none peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 transition-all -top-1.5 peer-placeholder-shown:text-base text-[12px] md:text-[13px] peer-focus:text-[12px] md:peer-focus:text-[13px] before:content[' '] before:block before:box-border before:w-2 md:before:w-[13px] before:h-1.5 before:mt-1.5 before:mr-1 peer-placeholder-shown:before:border-transparent before:rounded-tl-[4px] md:before:rounded-tl-[8px] md:before:translate-y-px before:border-t before:border-l before:pointer-events-none before:transition-all peer-disabled:before:border-transparent after:content[' '] after:block after:flex-grow after:box-border after:w-2.5 after:h-1.5 after:mt-1.5 after:ml-1 peer-placeholder-shown:after:border-transparent after:rounded-tr-[4px] md:after:rounded-tr-[8px] md:after:translate-y-px after:border-t after:border-r after:pointer-events-none after:transition-all peer-disabled:after:border-transparent peer-placeholder-shown:leading-[3.5] text-brand-green/[.5] peer-focus:text-link-blue before:border-[#dde1dd] peer-focus:before:!border-link-blue after:border-[#dde1dd] peer-focus:after:!border-link-blue">
                    {children}
                </label>
            )}
            {icon && (
                <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                    {icon}
                </div>
            )}
        </div>
    );
}
